/* eslint-disable import/no-deprecated -- file defines legacy redirects */
import { useMemo } from 'react';

import { AddContextualData } from '@opendoor/observability';
import { useLocation } from '@remix-run/react';

import { useAuth } from 'platform/auth-fe';
import { Navigate } from 'platform/remix-run/react';
import { maybeAppendCustomerUUIDOverride } from 'platform/useRedirectToLatestLead';

/**
 * NOTE: query param should be calculated as close to the url generation as possible. This is to ensure that any additions to the query params are included in the redirect.
 */
export const useAuthenticatedQueryParams = (customerUUIDOverride?: string) => {
  const { search } = useLocation();
  const auth = useAuth();
  const searchParams = useMemo(() => new URLSearchParams(search), [search]);

  return useMemo(
    () => maybeAppendCustomerUUIDOverride(searchParams, auth, customerUUIDOverride),
    [auth, customerUUIDOverride, searchParams],
  );
};

/**
 *  /dashboard/LEGACY_BASE_ROUTE/:addressUUID/SUB_ROUTE
 *  /----0----/--------1-------/------2------/----3----
 */
export const parseLegacyAddressUUIDSellRoute = (pathname: string) => {
  const splitPath = pathname.split('/').slice(1);
  const basePath = `/dashboard/sell`;
  const addressUUID = splitPath[2];
  const subPath =
    '/' + splitPath[1] + (splitPath.length > 3 ? `/${splitPath.slice(3).join('/')}` : '');
  return { addressUUID, basePath, subPath };
};

/**
 * @returns Redirects /{schedule|virtual-assessment|exterior-assessment}/customer_uuid/address_uuid routes to /address_uuid routes
 */
export const RedirectLegacyHubRoute: React.FC = () => {
  const { pathname } = useLocation();
  const { addressUUID, basePath, subPath } = parseLegacyAddressUUIDSellRoute(pathname);
  const queryParams = useAuthenticatedQueryParams();
  // Intentionally allowing all search params to propagate for JUST THIS redirect
  const customDisallowedSearchParams = useMemo(() => [], []);
  const navigateState = useMemo(() => ({ legacyRoute: subPath }), [subPath]);

  return (
    <AddContextualData data={{ topLevelRoute: 'legacy-schedule' }}>
      <Navigate
        to={`${basePath}/${addressUUID}${subPath}${queryParams}`}
        customDisallowedSearchParams={customDisallowedSearchParams}
        state={navigateState}
      />
    </AddContextualData>
  );
};
